// BasicTimelineChart.js
"use client";
import React, { useEffect, useRef } from "react";
import ApexCharts from "apexcharts";
import { Card } from "react-bootstrap";

const BasicTimelineChart = ({ timelineData }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

 const statusColors = {
  working: "#5adf8bc6",
  meeting: "#3b82f6",     
  break: "#facc15",
  idle: "#ef4444",
  offline: "#64748B",
};

  // Group timeline data by user for the chart
const buildSeries = (data) => {
  if (!data || data.length === 0) return [];

  const userGroups = {};

  data.forEach(segment => {
    if (!userGroups[segment.user_name]) {
      userGroups[segment.user_name] = [];
    }

    // Convert database time to local time
    const startDate = new Date(segment.start_time);
    const endDate = segment.end_time ? new Date(segment.end_time) : null;

    const timezoneOffset = startDate.getTimezoneOffset() * 60000;

    userGroups[segment.user_name].push({
      x: segment.user_name,
      y: [
        startDate.getTime() - timezoneOffset,
        endDate ? endDate.getTime() - timezoneOffset : startDate.getTime() // fallback for ongoing
      ],
      fillColor: statusColors[segment.status] || statusColors.offline,
      status: segment.status,
      startTime: segment.start_time,
      endTime: segment.end_time, // will be null for ongoing segments
      duration: segment.duration
    });
  });

  // Append a final offline segment if user has clocked out
  Object.keys(userGroups).forEach(user => {
    const segments = userGroups[user];
    const lastSegment = segments[segments.length - 1];

    if (lastSegment && lastSegment.status !== 'offline' && lastSegment.endTime) {
      const clockOutTime = lastSegment.clock_out_time || lastSegment.endTime; // Use actual clock-out if available
      const startDate = new Date(lastSegment.endTime);
      const endDate = new Date(clockOutTime);
      const timezoneOffset = startDate.getTimezoneOffset() * 60000;

      segments.push({
        x: user,
        y: [
          startDate.getTime() - timezoneOffset,
          endDate.getTime() - timezoneOffset
        ],
        fillColor: statusColors.offline,
        status: 'offline',
        startTime: lastSegment.endTime,
        endTime: clockOutTime,
        duration: 0
      });
    }
  });

  return Object.keys(userGroups).map(userName => ({
    name: userName,
    data: userGroups[userName]
  }));
};


  useEffect(() => {
    if (!chartRef.current) return;

    const options = {
      chart: {
        type: "rangeBar",
        height: 300,
        toolbar: { 
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true
          }
        },
        animations: { 
          enabled: true, 
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          }
        },
        events: {
          dataPointSelection: function(event, chartContext, config) {
            const seriesIndex = config.seriesIndex;
            const dataPointIndex = config.dataPointIndex;
            const segment = chartContext.w.config.series[seriesIndex].data[dataPointIndex];
            console.log("Selected segment:", segment);
          }
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "30%",
          rangeBarGroupRows: true,
          borderRadius: 4,
          distributed: false,
        },
      },
      xaxis: {
        type: "datetime",
        labels: { 
          style: { colors: "#64748B", fontSize: "11px" },
          datetimeFormatter: {
            hour: 'HH:mm',
            minute: 'HH:mm'
          }
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
  labels: { 
    style: { colors: "#334155", fontSize: "12px" }
  },
},
      grid: { 
        borderColor: "#E2E8F0",
        padding: {
          top: 10,
          right: 10,
          bottom: 10,
          left: 10
        }
      },
      tooltip: {
  custom: function({ seriesIndex, dataPointIndex, w }) {
    const series = w.config.series[seriesIndex];
    const data = series?.data[dataPointIndex];
    
    if (!data) return "";
    
    const start = new Date(data.startTime).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
    
    const end = new Date(data.endTime).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
    
    const startDate = new Date(data.startTime).toLocaleDateString();
    const endDate = new Date(data.endTime).toLocaleDateString();
    
    const minutes = Math.floor(data.duration / 60);
    const seconds = data.duration % 60;
    const duration = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    
    let dateInfo = '';
    if (startDate === endDate) {
      dateInfo = startDate;
    } else {
      dateInfo = `${startDate} - ${endDate}`;
    }
    
    // Only show End time if status is 'offline'
    const showEnd = data.status === 'offline';
    
    return `
      <div style="padding: 12px; background: white; border-radius: 6px; border: 1px solid #e2e8f0; box-shadow: 0 4px 12px rgba(0,0,0,0.1); max-width: 280px;">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #f1f5f9;">
          <div style="width: 14px; height: 14px; border-radius: 3px; background: ${data.fillColor};"></div>
          <strong style="font-size: 14px; color: #334155;">${data.x}</strong>
          <span style="font-size: 11px; background: #f1f5f9; color: #64748B; padding: 2px 6px; border-radius: 10px; margin-left: auto;">
            ${data.status.charAt(0).toUpperCase() + data.status.slice(1)}
          </span>
        </div>
        <div style="font-size: 12px; color: #64748B; line-height: 1.5;">
          <div style="margin-bottom: 4px; color: #475569;">
            <strong>Date:</strong> ${dateInfo}
          </div>
          <div style="margin-bottom: 4px;">
            <strong>Start:</strong> ${start}
          </div>
          ${showEnd ? `
          <div style="margin-bottom: 4px;">
            <strong>End:</strong> ${end}
          </div>
          ` : ''}
          <div>
            <strong>Duration:</strong> ${duration}
          </div>
        </div>
      </div>
    `;
  }
},
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left',
        markers: {
          radius: 6,
        },
        itemMargin: {
          horizontal: 20,
          vertical: 5
        },
        onItemClick: {
          toggleDataSeries: true
        },
        onItemHover: {
          highlightDataSeries: true
        }
      },
      stroke: {
        width: 1,
        colors: ['#fff']
      },
      dataLabels: {
        enabled: false,
      },
      fill: {
        type: "solid",
        opacity: 0.9,
      },
      responsive: [{
        breakpoint: 768,
        options: {
          chart: {
            height: 350
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '10px'
              }
            }
          }
        }
      }]
    };

    const series = buildSeries(timelineData);
    
    // Check if we need to update or create new chart
    if (!chartInstance.current) {
      chartInstance.current = new ApexCharts(chartRef.current, {
        ...options,
        series: series
      });
      chartInstance.current.render();
    } else {
      // Update only if data has changed
      const currentSeries = JSON.stringify(chartInstance.current.w.config.series);
      const newSeries = JSON.stringify(series);
      
      if (currentSeries !== newSeries) {
        chartInstance.current.updateSeries(series, true);
      }
    }

    // Handle window resize
    const handleResize = () => {
      if (chartInstance.current) {
        setTimeout(() => {
          chartInstance.current.update();
        }, 300);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [timelineData]);

  const getStatusCounts = () => {
    if (!timelineData || timelineData.length === 0) {
      return { working: 0, break: 0, idle: 0 };
    }
    
    const counts = { working: 0, break: 0, idle: 0 };
    timelineData.forEach(segment => {
      if (counts[segment.status] !== undefined) {
        counts[segment.status]++;
      }
    });
    
    return counts;
  };



  return (
    <Card className="bg-white border-0 rounded-3 mb-4 shadow-sm">
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h3 className="mb-1">Activity Timeline</h3>
           
          </div>
        </div>
        
        {(!timelineData || timelineData.length === 0) ? (
          <div className="text-center py-5 text-muted">
            <div className="mb-3">
              <i className="bi bi-clock-history" style={{ fontSize: '48px', opacity: 0.5 }}></i>
            </div>
            <h5>No timeline data available</h5>
            <p className="mb-0">Start tracking activities to see the timeline chart</p>
          </div>
        ) : (
          <div ref={chartRef} />
        )}
        
        <div className="mt-3 text-end">
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            Hover over segments to see details. Data refreshes automatically.
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

export default BasicTimelineChart;